import {
  ERP_READ_RESOURCE_CAPABILITIES,
  ERP_READ_RESOURCES,
  ERP_READ_SORTS,
  type ErpReadDeskQuery,
  type ErpReadDeskRecord,
  type ErpReadResource,
  type ErpReadSort,
} from './types'

const MAX_SEARCH_LENGTH = 120
const SEARCH_CONTROL_CHARACTERS = /[\p{Cc}\p{Cf}]/u
const SAFE_ID = /^\d{1,32}$/

function firstValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
  return undefined
}

function resource(value: unknown): ErpReadResource {
  const candidate = firstValue(value)
  return ERP_READ_RESOURCES.includes(candidate as ErpReadResource) ? candidate as ErpReadResource : 'orders'
}

function search(value: unknown): string {
  const candidate = firstValue(value) ?? ''
  if (candidate.length > MAX_SEARCH_LENGTH || SEARCH_CONTROL_CHARACTERS.test(candidate)) return ''
  return candidate.trim()
}

function statuses(value: unknown, currentResource: ErpReadResource): string[] {
  const candidate = firstValue(value)
  if (!candidate || currentResource === 'automation') return []
  const allowed = new Set(ERP_READ_RESOURCE_CAPABILITIES[currentResource].statuses.map(status => status.value))
  return [...new Set(candidate.split(',').map((entry) => entry.trim().toLowerCase()).filter((entry) => allowed.has(entry)))].sort()
}

function sort(value: unknown): ErpReadSort {
  const candidate = firstValue(value)
  return ERP_READ_SORTS.includes(candidate as ErpReadSort) ? candidate as ErpReadSort : 'updated_at_desc'
}

function limit(value: unknown, currentResource: ErpReadResource): number {
  const capabilities = ERP_READ_RESOURCE_CAPABILITIES[currentResource]
  const allowedLimits: readonly number[] = capabilities.limits
  const candidate = firstValue(value)
  if (!candidate || !/^\d+$/.test(candidate)) return capabilities.defaultLimit
  const parsed = Number(candidate)
  return Number.isSafeInteger(parsed) && allowedLimits.includes(parsed)
    ? parsed
    : capabilities.defaultLimit
}

function selectedId(value: unknown): string | null {
  const candidate = firstValue(value)
  return candidate && SAFE_ID.test(candidate) ? candidate : null
}

export function parseErpReadDeskQuery(raw: Record<string, unknown>): ErpReadDeskQuery {
  const currentResource = resource(raw.tab)
  return {
    resource: currentResource,
    search: search(raw.search),
    statuses: statuses(raw.status, currentResource),
    sort: sort(raw.sort),
    limit: limit(raw.limit, currentResource),
    variationManagerOnly: currentResource === 'products' && firstValue(raw.studio_only) === 'true',
    selectedId: selectedId(raw.selected),
  }
}

export function serializeErpReadDeskQuery(query: ErpReadDeskQuery): Record<string, string> {
  const serialized: Record<string, string> = {
    tab: query.resource,
    sort: query.sort,
  }
  if (ERP_READ_RESOURCE_CAPABILITIES[query.resource].pagination) serialized.limit = String(query.limit)
  if (query.search) serialized.search = query.search
  if (query.statuses.length > 0) serialized.status = query.statuses.join(',')
  if (query.resource === 'products' && query.variationManagerOnly) serialized.studio_only = 'true'
  if (query.selectedId) serialized.selected = query.selectedId
  return serialized
}

function textSortValue(record: ErpReadDeskRecord, sortBy: ErpReadSort): string {
  switch (sortBy) {
    case 'title_asc':
    case 'title_desc':
      return record.title?.toLocaleLowerCase() ?? ''
    case 'name_asc':
    case 'name_desc':
      return record.name?.toLocaleLowerCase() ?? ''
    default:
      return ''
  }
}

function dateSortValue(record: ErpReadDeskRecord, sortBy: ErpReadSort): number {
  const candidate = sortBy.startsWith('created_at')
    ? record.created_at
    : record.updated_at ?? record._erp_updated_at
  const parsed = typeof candidate === 'string' ? Date.parse(candidate) : Number.NaN
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
}

function compareNumbers(left: number, right: number): number {
  if (left === right) return 0
  return left < right ? -1 : 1
}

export function sortCurrentPage<T extends ErpReadDeskRecord>(records: readonly T[], sortBy: ErpReadSort): T[] {
  const descending = sortBy.endsWith('_desc')
  const dateSort = sortBy.startsWith('created_at') || sortBy.startsWith('updated_at')
  return [...records].sort((left, right) => {
    const compared = (
      dateSort
        ? compareNumbers(dateSortValue(left, sortBy), dateSortValue(right, sortBy))
        : textSortValue(left, sortBy).localeCompare(textSortValue(right, sortBy))
    ) || left.id.localeCompare(right.id)
    return descending ? -compared : compared
  })
}
